import { useState } from 'react'
import { styled } from 'styled-components'
import { addStudent } from '../../../libs/student-service'
import { IoClose } from 'react-icons/io5'

interface Props {
    classId: number
    onClose: () => void
    onConfirm: () => void
}

const Overlay = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Modal = styled.div`
    background: white;
    border-radius: 12px;
    max-width: 550px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid #f0f0f0;
`

const Title = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: #000000;
    margin: 0;
`

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999999;
    transition: color 0.2s;

    &:hover {
        color: #333333;
    }

    svg {
        width: 24px;
        height: 24px;
    }
`

const Content = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const Label = styled.label`
    font-size: 14px;
    font-weight: 500;
    color: #666666;
`

const Input = styled.input`
    padding: 12px 16px;
    border: 1px solid #1fa474;
    border-radius: 6px;
    font-size: 14px;
    background-color: #ffffff;
    color: #333333;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: #1fa474;
        box-shadow: 0 0 0 2px rgba(31, 164, 116, 0.1);
    }

    &::placeholder {
        color: #999999;
    }
`

const Select = styled.select`
    padding: 12px 16px;
    border: 1px solid #1fa474;
    border-radius: 6px;
    font-size: 14px;
    background-color: #ffffff;
    color: #333333;
    cursor: pointer;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: #1fa474;
        box-shadow: 0 0 0 2px rgba(31, 164, 116, 0.1);
    }
`

const Footer = styled.div`
    padding: 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`

const Button = styled.button`
    padding: 12px 32px;
    border: none;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`

const CancelButton = styled(Button)`
    background-color: #f0f0f0;
    color: #333333;

    &:hover:not(:disabled) {
        background-color: #e0e0e0;
    }
`

const ConfirmButton = styled(Button)`
    background-color: #1fa474;
    color: white;

    &:hover:not(:disabled) {
        background-color: #168a5d;
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }
`

const ErrorMessage = styled.div`
    padding: 12px 16px;
    background-color: #fee;
    border: 1px solid #fcc;
    border-radius: 6px;
    color: #c33;
    font-size: 14px;
`

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`

export default function AddStudentModal({
    classId,
    onClose,
    onConfirm,
}: Props) {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        student_id: '',
        gender: '',
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))
        setError(null)
    }

    const validateForm = (): boolean => {
        if (!formData.first_name.trim()) {
            setError('First name is required')
            return false
        }
        if (!formData.last_name.trim()) {
            setError('Last name is required')
            return false
        }
        if (!formData.student_id.trim()) {
            setError('Student ID is required')
            return false
        }
        return true
    }

    const handleConfirm = async () => {
        if (!validateForm()) return

        setLoading(true)
        setError(null)

        try {
            await addStudent({
                class_id: classId,
                first_name: formData.first_name,
                last_name: formData.last_name,
                student_id: formData.student_id,
                gender: formData.gender,
            })

            onConfirm()
            onClose()
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'Failed to add student'
            setError(errorMessage)
            console.error('Failed to add student:', err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <Header>
                    <Title>Add New Student</Title>
                    <CloseButton onClick={onClose}>
                        <IoClose />
                    </CloseButton>
                </Header>

                <Content>
                    <FormGroup>
                        <Label>First Name *</Label>
                        <Input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleInputChange}
                            placeholder="Enter first name"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Last Name *</Label>
                        <Input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            placeholder="Enter last name"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Student ID *</Label>
                        <Input
                            type="text"
                            name="student_id"
                            value={formData.student_id}
                            onChange={handleInputChange}
                            placeholder="Enter student ID"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Gender</Label>
                        <Select
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                        >
                            <option value="">Select gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Prefer not to say">
                                Prefer not to say
                            </option>
                        </Select>
                    </FormGroup>

                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </Content>

                <Footer>
                    <ButtonGroup>
                        <CancelButton onClick={onClose} disabled={loading}>
                            Cancel
                        </CancelButton>
                        <ConfirmButton
                            onClick={handleConfirm}
                            disabled={loading}
                        >
                            {loading ? 'Adding...' : 'Add Student'}
                        </ConfirmButton>
                    </ButtonGroup>
                </Footer>
            </Modal>
        </Overlay>
    )
}
